import React, { useEffect, useMemo, useState } from 'react';
import { BookOpenCheck, Loader2, Download, Check, X, Minus, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminGetManual, adminDownloadManualPdf, type Manual } from '../../features/manual/api/manual';

function RoleBadge({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border"
      style={{ backgroundColor: `${color}1f`, color, borderColor: `${color}66` }}
    >
      {label}
    </span>
  );
}

function MatrixCell({ value }: { value: 'yes' | 'no' | 'limited' }) {
  if (value === 'yes') return <Check className="w-4 h-4 text-emerald-400 mx-auto" strokeWidth={2.5} />;
  if (value === 'no') return <X className="w-4 h-4 text-red-400/70 mx-auto" strokeWidth={2.5} />;
  return <Minus className="w-4 h-4 text-amber-400 mx-auto" strokeWidth={2.5} />;
}

export default function AdminManualPage() {
  const [manual, setManual] = useState<Manual | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [openRole, setOpenRole] = useState<string>('admin');

  useEffect(() => {
    adminGetManual()
      .then((m) => { setManual(m); })
      .catch((e: any) => toast.error('Gagal memuat', { description: e.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
  }, []);

  const roleMap = useMemo(() => {
    const map: Record<string, { label: string; color: string }> = {};
    manual?.roles.forEach((r) => { map[r.key] = { label: r.label, color: r.color }; });
    return map;
  }, [manual]);

  const matrixHeader: Record<string, string> = { ...Object.fromEntries((manual?.roles || []).map((r) => [r.key, r.label])), webinar: 'Webinar', workshop: 'Workshop' };

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await adminDownloadManualPdf();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'manual-book-admin-cuantrader.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF diunduh');
    } catch (e: any) {
      toast.error('Gagal mengunduh PDF', { description: e.response?.data?.detail || e.message });
    } finally {
      setDownloading(false);
    }
  };

  const toc = [
    { id: 'pengenalan', label: '1. Pengenalan' },
    { id: 'matrix', label: '2. Tabel Hak Akses' },
    { id: 'peran', label: '3. Panduan per Peran' },
    { id: 'istilah', label: '4. Istilah & FAQ' },
  ];
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Manual Book">
      <div data-testid="admin-manual-page">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <BookOpenCheck className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Panduan Internal</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Manual Book Admin</h2>
            </div>
          </div>
          <button
            data-testid="manual-download-pdf"
            onClick={download}
            disabled={downloading || loading}
            className="bg-cyan-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Download className="w-4 h-4" strokeWidth={2} />}
            Unduh PDF
          </button>
        </div>

        {loading || !manual ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[210px_1fr] gap-8">
            {/* TOC */}
            <aside className="hidden lg:block">
              <div className="sticky top-6 bg-white/[0.02] border border-white/10 rounded-2xl p-4">
                <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Daftar Isi</p>
                <nav className="space-y-1">
                  {toc.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`manual-toc-${t.id}`}
                      onClick={() => scrollTo(t.id)}
                      className="block w-full text-left text-sm text-white/60 hover:text-cyan-400 hover:bg-white/5 rounded-lg px-2.5 py-1.5 transition-colors"
                    >
                      {t.label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="space-y-10 max-w-3xl">
              {/* 1. Pengenalan */}
              <section id="pengenalan" className="scroll-mt-6">
                <h3 className="text-xl font-medium text-white mb-3">1. Pengenalan</h3>
                <div className="space-y-3 text-sm text-white/70 leading-relaxed">
                  {manual.intro.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <div className="mt-5 bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-3">
                  <p className="text-xs uppercase tracking-wider text-white/40">Daftar Peran</p>
                  {manual.roles.map((r) => (
                    <div key={r.key} className="flex flex-wrap items-center gap-2 text-sm text-white/70">
                      <RoleBadge label={r.label} color={r.color} />
                      <span>{r.one_liner}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* 2. Matrix */}
              <section id="matrix" className="scroll-mt-6">
                <h3 className="text-xl font-medium text-white mb-2">2. Tabel Hak Akses (Matrix)</h3>
                <p className="text-sm text-white/50 mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-emerald-400" /> Bisa</span>
                  <span className="inline-flex items-center gap-1"><X className="w-4 h-4 text-red-400/70" /> Tidak bisa</span>
                  <span className="inline-flex items-center gap-1"><Minus className="w-4 h-4 text-amber-400" /> Terbatas</span>
                </p>
                <div className="overflow-x-auto border border-white/10 rounded-2xl">
                  <table className="w-full text-sm" data-testid="manual-matrix-table">
                    <thead>
                      <tr className="bg-[#0b1324]">
                        <th className="text-left text-white/70 font-medium px-4 py-3 min-w-[200px]">Fitur</th>
                        {manual.role_order.map((k) => (
                          <th key={k} className="text-center text-white/70 font-medium px-2 py-3 whitespace-nowrap text-xs">{matrixHeader[k]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {manual.matrix_features.map((row, i) => (
                        <tr key={i} className={i % 2 ? 'bg-white/[0.015]' : ''}>
                          <td className="px-4 py-2.5 text-white/70">{row.feature}</td>
                          {manual.role_order.map((k) => (
                            <td key={k} className="px-2 py-2.5 text-center"><MatrixCell value={row.values[k]} /></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* 3. Panduan per Peran */}
              <section id="peran" className="scroll-mt-6">
                <h3 className="text-xl font-medium text-white mb-4">3. Panduan per Peran</h3>
                <div className="space-y-3">
                  {manual.role_guides.map((g) => {
                    const meta = roleMap[g.role];
                    const open = openRole === g.role;
                    return (
                      <div key={g.role} className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
                        <button
                          data-testid={`manual-role-toggle-${g.role}`}
                          onClick={() => setOpenRole(open ? '' : g.role)}
                          className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-white/[0.03] transition-colors"
                        >
                          <RoleBadge label={meta?.label || g.role} color={meta?.color || '#38bdf8'} />
                          <ChevronDown className={`w-5 h-5 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                        {open && (
                          <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
                            <p className="text-sm text-white/70 leading-relaxed">{g.intro}</p>
                            <div>
                              <p className="text-xs uppercase tracking-wider text-emerald-400/80 mb-2">Yang bisa dilakukan</p>
                              <ul className="space-y-1.5">
                                {g.can.map((c, i) => (
                                  <li key={i} className="flex gap-2 text-sm text-white/70"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" strokeWidth={2} />{c}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wider text-red-400/70 mb-2">Yang tidak bisa</p>
                              <ul className="space-y-1.5">
                                {g.cannot.map((c, i) => (
                                  <li key={i} className="flex gap-2 text-sm text-white/70"><X className="w-4 h-4 text-red-400/70 shrink-0 mt-0.5" strokeWidth={2} />{c}</li>
                                ))}
                              </ul>
                            </div>
                            {g.tasks.length > 0 && (
                              <div className="space-y-4">
                                <p className="text-xs uppercase tracking-wider text-cyan-400/80">Langkah tugas utama</p>
                                {g.tasks.map((t, ti) => (
                                  <div key={ti} className="bg-[#050914] border border-white/10 rounded-xl p-4">
                                    <p className="text-sm font-medium text-white mb-2">{t.title}</p>
                                    <ol className="space-y-1.5">
                                      {t.steps.map((st, si) => (
                                        <li key={si} className="flex gap-2.5 text-sm text-white/70">
                                          <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-400/15 text-cyan-400 text-xs flex items-center justify-center font-medium">{si + 1}</span>
                                          {st}
                                        </li>
                                      ))}
                                    </ol>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* 4. Istilah & FAQ */}
              <section id="istilah" className="scroll-mt-6">
                <h3 className="text-xl font-medium text-white mb-4">4. Istilah & FAQ Singkat</h3>
                <div className="space-y-3">
                  {manual.faq.map((f, i) => (
                    <div key={i} className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                      <p className="text-sm font-medium text-cyan-400 mb-1.5">{f.term}</p>
                      <p className="text-sm text-white/70 leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
